import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * Colour theme system (Dark · Classic White · Steel · Warm Paper).
 *
 * Two modes:
 *   manual — the picked theme is used everywhere.
 *   auto   — "Follow device": Dark while the phone / laptop is in dark mode,
 *            the chosen light theme (`lightTheme`) while it's in light mode.
 *
 * The effective key is written to `<html data-color-theme="...">`; every token
 * set lives in src/themes.css. Choices are cached per device (to avoid a flash
 * before /auth/me answers) and saved on the user's account so they follow them
 * across desktop and mobile (see AuthContext + ThemePicker).
 */
const STORAGE_KEY = "g5-color-theme"; // manual pick
const MODE_KEY = "g5-theme-mode"; // "manual" | "auto"
const LIGHT_KEY = "g5-light-theme"; // light theme used by day in auto mode

export const DEFAULT_COLOR_THEME = "builtos"; // what a brand-new device sees before signing in
export const DEFAULT_LIGHT_THEME = "builtos"; // daytime theme when following the device
export const NIGHT_THEME = "dark"; // always the night-time theme when following the device
export const THEME_MODES = ["manual", "auto"];

export const COLOR_THEMES = [
  {
    key: "builtos",
    name: "BuiltOS Blue",
    tagline: "Default",
    description: "Soft white and concrete grey with deep navy and steel blue — the BuiltOS signature look.",
    tone: "light",
    swatch: { bg: "#F7F9FB", surface: "#FFFFFF", text: "#1F2A3A", muted: "#475569", primary: "#0D376D", border: "#DAE0E8" },
  },
  {
    key: "dark",
    name: "Dark",
    tagline: "Night",
    description: "The charcoal & bronze look — easy on the eyes at night and in the office.",
    tone: "dark",
    // swatches drive the picker preview (bg · surface · text · primary · accent)
    swatch: { bg: "#111111", surface: "#1B1B1B", text: "#F5F5F5", muted: "#B8B8B8", primary: "#B8864B", border: "#2E2E2E" },
  },
  {
    key: "light",
    name: "Classic White",
    tagline: "Clean & modern",
    description: "Off-white background with charcoal text — crisp, high-contrast and bright.",
    tone: "light",
    swatch: { bg: "#FAF9F6", surface: "#FFFFFF", text: "#212121", muted: "#5C5C5C", primary: "#96683A", border: "#DAD6CF" },
  },
  {
    key: "steel",
    name: "Steel",
    tagline: "Industrial",
    description: "Light grey / slate palette with a construction-pro edge and bronze accents.",
    tone: "light",
    swatch: { bg: "#E8ECF0", surface: "#F6F7F9", text: "#18202B", muted: "#4B5563", primary: "#91653A", border: "#C3CBD5" },
  },
  {
    key: "paper",
    name: "Warm Paper",
    tagline: "Soft cream",
    description: "A soft cream background that's easier on the eyes for long office sessions.",
    tone: "light",
    swatch: { bg: "#F5EFE3", surface: "#FBF8F1", text: "#2B241C", muted: "#6B5F52", primary: "#8F6236", border: "#DCD1BF" },
  },
];

const VALID_KEYS = new Set(COLOR_THEMES.map((t) => t.key));
const LIGHT_KEYS = new Set(COLOR_THEMES.filter((t) => t.tone === "light").map((t) => t.key));
const listeners = new Set();

export const isValidColorTheme = (key) => VALID_KEYS.has(key);
export const isLightColorTheme = (key) => LIGHT_KEYS.has(key);
export const isValidThemeMode = (mode) => THEME_MODES.includes(mode);
export const themeMeta = (key) => COLOR_THEMES.find((t) => t.key === key) || COLOR_THEMES[0];

// Immutable snapshot — replaced wholesale on every change so useSyncExternalStore can compare by reference.
let state = {
  theme: DEFAULT_COLOR_THEME, // manual pick
  mode: "manual",
  lightTheme: DEFAULT_LIGHT_THEME,
  systemDark: true, // what the device is asking for right now
};
let pinnedKey = null; // set while a branded page (client portal) forces a theme
let mediaQuery = null;

const safeGet = (k) => {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
};
const safeSet = (k, v) => {
  try {
    window.localStorage.setItem(k, v);
  } catch {
    /* private browsing — theme still applies for the session */
  }
};

/** The key that should actually be on <html> for the current state. */
export const resolveColorTheme = (s = state) =>
  s.mode === "auto" ? (s.systemDark ? NIGHT_THEME : s.lightTheme) : s.theme;

const applyToDom = (key) => {
  const root = document.documentElement;
  root.setAttribute("data-color-theme", key);
  const meta = themeMeta(key);
  root.style.colorScheme = meta.tone === "light" ? "light" : "dark"; // native form controls / scrollbars follow
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute("content", meta.swatch.bg);
};

const applyEffective = () => applyToDom(pinnedKey || resolveColorTheme());

const commit = (next) => {
  state = { ...state, ...next };
  applyEffective();
  listeners.forEach((l) => l());
};

const readSystemDark = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const readStored = () => {
  const theme = safeGet(STORAGE_KEY);
  const mode = safeGet(MODE_KEY);
  const light = safeGet(LIGHT_KEY);
  return {
    theme: VALID_KEYS.has(theme) ? theme : DEFAULT_COLOR_THEME,
    mode: isValidThemeMode(mode) ? mode : "manual",
    lightTheme: LIGHT_KEYS.has(light) ? light : DEFAULT_LIGHT_THEME,
  };
};

const onSystemChange = (e) => {
  const dark = typeof e?.matches === "boolean" ? e.matches : readSystemDark();
  if (dark === state.systemDark) return;
  commit({ systemDark: dark });
};

/** Call once on boot (before render) to avoid a theme flash. */
export function initColorTheme() {
  state = { ...state, ...readStored(), systemDark: readSystemDark() };
  applyEffective();
  if (typeof window !== "undefined" && typeof window.matchMedia === "function" && !mediaQuery) {
    mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    if (typeof mediaQuery.addEventListener === "function") mediaQuery.addEventListener("change", onSystemChange);
    else if (typeof mediaQuery.addListener === "function") mediaQuery.addListener(onSystemChange); // older Safari
  }
}

/** Pick a theme manually (also switches out of Follow-device). Account persistence is handled by the caller. */
export function setColorTheme(key) {
  if (!VALID_KEYS.has(key)) return;
  const next = {
    theme: key,
    mode: "manual",
    lightTheme: LIGHT_KEYS.has(key) ? key : state.lightTheme, // remember the last light pick as the daytime theme
  };
  if (next.theme === state.theme && next.mode === state.mode && next.lightTheme === state.lightTheme) return;
  safeSet(STORAGE_KEY, next.theme);
  safeSet(MODE_KEY, next.mode);
  safeSet(LIGHT_KEY, next.lightTheme);
  commit(next);
}

/** Switch between manual and Follow-device. */
export function setThemeMode(mode) {
  if (!isValidThemeMode(mode) || mode === state.mode) return;
  safeSet(MODE_KEY, mode);
  if (mode === "auto") safeSet(LIGHT_KEY, state.lightTheme); // so the next boot resolves the same daytime theme
  commit({ mode });
}

/** The light theme used by day when following the device. */
export function setLightTheme(key) {
  if (!LIGHT_KEYS.has(key) || key === state.lightTheme) return;
  safeSet(LIGHT_KEY, key);
  commit({ lightTheme: key });
}

/** Apply the account's saved preferences in one go (AuthContext, after /auth/me or login). */
export function applyColorThemePrefs({ color_theme, theme_mode, light_theme } = {}) {
  const next = {};
  if (VALID_KEYS.has(color_theme)) {
    next.theme = color_theme;
    safeSet(STORAGE_KEY, color_theme);
  }
  if (isValidThemeMode(theme_mode)) {
    next.mode = theme_mode;
    safeSet(MODE_KEY, theme_mode);
  }
  if (LIGHT_KEYS.has(light_theme)) {
    next.lightTheme = light_theme;
    safeSet(LIGHT_KEY, light_theme);
  }
  if (!Object.keys(next).length) return;
  commit(next);
}

const subscribe = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
const getSnapshot = () => state;
const SERVER_SNAPSHOT = { theme: DEFAULT_COLOR_THEME, mode: "manual", lightTheme: DEFAULT_LIGHT_THEME, systemDark: true };
const getServerSnapshot = () => SERVER_SNAPSHOT;

export function useColorTheme() {
  const s = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const theme = resolveColorTheme(s);
  const meta = themeMeta(theme);
  const setTheme = useCallback((k) => setColorTheme(k), []);
  const setMode = useCallback((m) => setThemeMode(m), []);
  const setLight = useCallback((k) => setLightTheme(k), []);
  return {
    theme, // effective key on <html>
    manualTheme: s.theme,
    mode: s.mode,
    isAuto: s.mode === "auto",
    lightTheme: s.lightTheme,
    systemDark: s.systemDark,
    setTheme,
    setMode,
    setLightTheme: setLight,
    themes: COLOR_THEMES,
    isLight: meta.tone === "light",
    meta,
  };
}

/**
 * Pin the current page to a fixed theme (the branded client portal is always
 * charcoal & bronze) without touching the signed-in user's saved choice.
 * Restores the user's theme when the page unmounts.
 */
export function usePinnedColorTheme(key = NIGHT_THEME) {
  useEffect(() => {
    if (!VALID_KEYS.has(key)) return undefined;
    pinnedKey = key;
    applyEffective();
    document.documentElement.setAttribute("data-color-theme-pinned", key);
    return () => {
      pinnedKey = null;
      document.documentElement.removeAttribute("data-color-theme-pinned");
      applyEffective();
    };
  }, [key]);
}
